/**
 * Deterministic Query Engine - Shared Types
 */

export type ObjectType =
  | 'task' | 'event' | 'contact' | 'note'
  | 'place' | 'asset' | 'stat' | 'memory';

/**
 * Minimal object representation for deterministic queries.
 * Reuses existing frontend object shape where possible.
 */
export interface ConversationJsonObject {
  id: string;
  type: ObjectType;
  // Display fields (varies by type)
  name?: string;        // contacts, places
  title?: string;       // tasks, events, notes
  // Task fields
  status?: string;      // tasks: 'todo' | 'in_progress' | 'done'
  priority?: string;    // tasks: 'low' | 'medium' | 'high'
  dueAt?: string;       // tasks: ISO date
  // Event fields
  startAt?: string;     // events: ISO datetime
  endAt?: string;       // events: ISO datetime
  location?: string;    // events: venue/address
  // Contact fields
  email?: string;       // contacts: primary email
  emails?: string[];    // contacts: all emails
  phone?: string;       // contacts: primary phone
  phones?: string[];    // contacts: all phones
  company?: string;     // contacts: organization
  organization?: string; // contacts: organization (alias)
  stage?: string;       // contacts: CRM stage
  summary?: string;     // contacts: brief description/bio
  tags?: string[];      // contacts: tags for categorization
  nextStep?: { action: string; dueAt: string }; // contacts: next follow-up action
  dealValue?: number;   // contacts: deal value in dollars
  probability?: number; // contacts: probability of close (0-100)
  leadStatus?: string;  // contacts: lead status (hot_lead, warm_prospect, etc.)
  notes?: Array<{ id: string; content: string; createdAt: string }>; // contacts: interaction notes
  // Note/Journal fields
  content?: string;     // notes: body text
  body?: string;        // notes: alias for content
  mood?: string;        // journals: mood
  // Stat/Health fields (Fred's health tracking)
  value?: string | number;  // stats: the reading value (e.g., "120/80", 180)
  unit?: string;            // stats: unit of measurement (e.g., "mmHg", "lbs")
  recordedAt?: string;      // stats: when the measurement was taken
  // Memory fields (AI-learned facts about the user)
  category?: string;        // memories: category (family, preference, etc.)
  keywords?: string[];      // memories: keywords for matching
  sourceConversationId?: string;   // memories: source conversation id
  sourceConversationName?: string; // memories: which conversation the memory was learned from
  // Common fields
  description?: string;
  updatedAt: string;
  createdAt: string;
  // Contact display-name override (when set, shown verbatim instead of auto-cased name)
  displayNameOverride?: string | null;
  // Raw attributes for edge cases
  attributes?: Record<string, unknown>;
}

/**
 * Complete conversation data snapshot for deterministic queries.
 * Fetched once and cached for quick pattern matching.
 */
export interface ConversationJson {
  conversation: {
    id: string;
    name: string;
    modules?: Array<{
      id: string;
      type: string;
      label: string;
      objectType?: string;
    }>;
  };
  objects: Partial<Record<ObjectType, ConversationJsonObject[]>>;
  summary: {
    totalObjects: number;
    byType: Partial<Record<ObjectType, number>>;
    truncatedTypes: ObjectType[];  // Types that hit limit
    moduleCount: number;
  };
  schemaHash: string;  // For cache invalidation
  fetchedAt: number;   // Unix timestamp
}

/** Maximum objects to fetch per type (prevents huge payloads) */
export const OBJECTS_PER_TYPE_LIMIT = 200;

/** How long before conversation data is considered stale */
export const STALE_TIME_MS = 30_000; // 30 seconds

/** All backend object types */
export const ALL_OBJECT_TYPES: ObjectType[] = [
  'task', 'event', 'contact', 'note', 'journal', 'place', 'asset', 'log', 'stat'
];
